import { useEffect } from 'react'
import { Flex, Spinner, Text } from '@chakra-ui/react'

import { setTokenRenewedHandler, setUnauthorizedHandler } from '@/lib/apiClient'
import { getAccessToken, isAccessTokenValid, setSession } from '@/services/jwt.service'
import { useAuthStore } from '@/store/authStore'

const AuthBootstrapping = () => (
  <Flex
    minHeight="100dvh"
    align="center"
    justify="center"
    gap="2"
    bg="page.bg"
    role="status"
    aria-live="polite"
  >
    <Spinner size="sm" color="text.muted" borderWidth="2px" />
    <Text textStyle="bodySecondary">Restoring your session…</Text>
  </Flex>
)

/**
 * Wraps the entire application. On mount it restores a session from the stored
 * access token before any route renders, so a signed-in visitor is never briefly
 * bounced to the login screen on a hard refresh.
 *
 * Children stay unmounted until the check settles.
 */
const Auth = ({ children }) => {
  const tokenLoading = useAuthStore((state) => state.tokenLoading)
  const loginWithToken = useAuthStore((state) => state.loginWithToken)
  const logoutService = useAuthStore((state) => state.logoutService)

  /*
   * A 401 now means "the access token expired" far more often than "the
   * session is over": the transport layer refreshes once before giving up.
   * This stores whatever it gets back, so the new token survives a reload.
   */
  useEffect(() => {
    setTokenRenewedHandler((accessToken) => setSession(accessToken))
    return () => setTokenRenewedHandler(null)
  }, [])

  // Only reached once a refresh has already failed, so the session really is
  // over; the route guards handle the redirect.
  useEffect(() => {
    setUnauthorizedHandler(() => logoutService())
    return () => setUnauthorizedHandler(null)
  }, [logoutService])

  useEffect(() => {
    const accessToken = getAccessToken()

    // No token, or one that already expired: nothing to restore.
    if (!isAccessTokenValid(accessToken)) {
      logoutService()
      return
    }

    setSession(accessToken)
    loginWithToken()
  }, [loginWithToken, logoutService])

  if (tokenLoading) return <AuthBootstrapping />

  return children
}

export default Auth
